import React from 'react'
import { Box, Button, Typography, styled } from '@mui/material'
import { CalendarMonth, Science, Pool, Star } from '@mui/icons-material'
import 'react-slideshow-image/dist/styles.css'
import Pic from '../../resources/IMG_8310.png'
import Filter2 from '../../resources/filter1.png'
import GreenPic3 from '../../resources/green3.jpeg'
import nicePic from '../../resources/nice_pool.jpeg'
import { Column, Row, SwapRow } from '../../components/Common/CommonComponents'
import { useNavigate } from 'react-router'
import Paths from '../../common/Paths'
import Footer from '../../components/Footer/Footer'

const PageContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  justifyItems: 'flex-start'
}))

const StarIcon = styled(Star)(({theme}) => ({
  color: theme.palette.tertiary.main,
}))

const PicWithTextContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '520px',
  display: 'flex',
  flexDirection: 'column',
  backgroundImage: `url(${nicePic})`,
  backgroundSize: 'cover',
  backgroundPosition: 'relative',
  backgroundBlendMode: 'soft-light',
  loading: 'eager',
  alignItems: 'flex-start',
  backgroundColor: 'rgba(0, 0, 0, .4)',
}))

const PicContainer = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  flex: 1,
}))

const TextContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '36px 25px'
}))

const InfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.grey[50],
  padding: '24px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.grey[300]}`,
  gap: '24px',
  flex: '1',
}))

const IconContainer = styled(Box)<{ color?: string }>(({ theme, color }) => ({
  backgroundColor: color === 'red' ? theme.palette.secondary.shade?.p4 : theme.palette.primary.shade?.p4,
  color: color === 'red' ? theme.palette.secondary.main : theme.palette.primary.main,
  padding: '8px',
  borderRadius: '12px',
  display: 'inline-block',
}))

const HomePage = () => {
  const navigate = useNavigate()
  
  return (
  <PageContainer>
    <PicWithTextContainer>
      <Column padding='100px 12px 0px' gap="12px">
        <Typography variant='h3' color="common.white" sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.7)', lineHeight: { xs: '46px', sm: '18px' } }}>
          Enjoy your pool,
        </Typography>
        <Typography variant='h3' color="tertiary.main" sx={{ textShadow: '1px 1px 1px rgba(0,0,0,0.7)', lineHeight: { xs: '46px', sm: '60epx' } }}>
          We'll handle the rest.
        </Typography>
        <Typography variant='subtitle1' color="common.white" sx={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>
          Experience total clarity with our precision chemical balancing and expert maintenance services. We don't just clean; we care for your aquatic investment.
        </Typography>
        <Box marginTop="18px">
        <Button 
          color="secondary"
          onClick={() => navigate(Paths.contact)}
          variant='contained'
          >
          <Typography variant='buttonSmall'>
            Get a Free Quote
          </Typography>
        </Button>
          </Box>
      </Column>
    </PicWithTextContainer>
    <Box flex="1" sx={(theme) => ({ backgroundColor: theme.palette.background.default })}>
      <Column>
        <TextContainer sx={(theme) => ({ backgroundColor: theme.palette.background.default })}>
          <Typography variant='h5'>
            Professional Standards
          </Typography>
          <Typography variant='subtitle1' color="text.secondary">
            Our streamlined process ensures your pool stays healthy, safe, and sparkling year-round.
          </Typography>
       <SwapRow size="md" gap="12px" width="100%" marginTop="12px">
          <InfoContainer>
            <Box>
              <IconContainer>
                <CalendarMonth />
              </IconContainer>
            </Box>
            <Column>
              <Typography variant='subtitle1' color='text.primary'>Book Your Visit</Typography>
              <Typography variant='body1' color='text.primary'>Schedule a one-time deep clean or regular maintenance.</Typography>
            </Column>
          </InfoContainer>
          <InfoContainer>
            <Box>
              <IconContainer color="red">
                <Science />
              </IconContainer>
            </Box>
            <Column>
              <Typography variant='subtitle1' color='text.primary'>Water Analysis</Typography>
              <Typography variant='body1' color='text.primary'>Our technicians use precise testing to balance your water with molecular accuracy and care.</Typography>
            </Column>
          </InfoContainer>
          <InfoContainer>
            <Box>
              <IconContainer>
                <Pool />
              </IconContainer>
            </Box>
            <Column>
              <Typography variant='subtitle1' color='text.primary'>Pure Enjoyment</Typography>
              <Typography variant='body1' color='text.primary'>Step into a pristine aquatic environment, managed by experts while you relax and unwind.</Typography>
            </Column>
          </InfoContainer>
        </SwapRow>
        </TextContainer>
        <Box 
          flex="1"
          padding="24px 12px"
          sx={(theme) => ({ backgroundColor: theme.palette.grey[50], borderTop: `1px solid ${theme.palette.grey[300]}`, borderBottom: `1px solid ${theme.palette.grey[300]}` })}
        >
          <TextContainer>
            <Typography variant="body2" color="secondary.main" marginBottom="8px">
              OUR HERITAGE
            </Typography>
            <Box margin="8px 0 14px">
              <Typography variant='h5' color="text.primary">
                Family Owned for
              </Typography>
              <Typography variant='h5' color="tertiary.main">
                Over 30 Years
              </Typography>
            </Box>
            <Typography variant='subtitle1' color="text.secondary">
              Since 1994, All Around Pool Cleaning has been more than just a service provider; we've been a staple of reliability in our community. What started as a small family operation has grown into a premier pool care firm, yet our core values remain unchanged.
            </Typography>
            <br />
            <Typography variant='subtitle1' color="text.secondary">
              We believe that a pool is the heart of a home’s outdoor life. Our personal commitment to your aquatic investment is rooted in decades of expertise and a heritage of trust. When you choose us, you're not just getting a technician—you're getting a legacy of excellence.
            </Typography>
          </TextContainer>
        </Box>
        <Box 
          flex="1"
          padding="24px 12px"
          sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
        >
          <TextContainer>
            <Typography variant='h5'>
              Our Portfolio
            </Typography>
            <Typography variant='subtitle1' color="text.secondary">
              See the All Around difference in every backyard we manage. From residential pools to commercial lap pools.            </Typography>
          </TextContainer>
          <SwapRow size="md" gap="10px">
            <PicContainer>
              <img src={Pic} height="400px" width="100%" alt="sample" style={{ objectFit: "cover", borderRadius: '12px' }} />
            </PicContainer>
            <PicContainer>
              <img src={Filter2} height="400px" width="100%" alt="sample" style={{ objectFit: "cover", borderRadius: '12px' }} />
            </PicContainer>
            <PicContainer>
              <img src={GreenPic3} height="400px" width="100%" alt="sample" style={{ objectFit: "cover", borderRadius: '12px' }} />
            </PicContainer>
          </SwapRow>
        </Box>
      </Column>
    </Box>
        <Box 
          flex="1"
          padding="46px 36px"
          sx={(theme) => ({ backgroundColor: theme.palette.primary.dark })}
        >
            <Typography variant="body2" color="tertiary.main" marginBottom="8px">
              CLIENT REVIEWS
            </Typography>
            <Typography variant='h5' color="common.white" marginBottom="12px">
              "We have been using All around pool cleaning service for regular maintenance, Nick does an outstanding job, our pool always looks crystal and perfectly balanced after every visit. Nick pays attention to the little details, brushing the walls, emptying baskets. Checking chemicals. I would recommend Nick and his service to anybody."
            </Typography>

            <Box 
              sx={(theme) => ({ 
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                padding: "24px",
                borderRadius: "8px",
                marginTop: "12px",
              })}
            >
              <Row gap="4px" marginBottom="12px">
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
              </Row>
              <Typography variant='h6' color="common.white" marginBottom="12px">
                "Nick has been our pool guy for almost 3 years. A really nice person who always leaves the pool looking amazing. If I ever have a problem, I text him and he gets straight back to me. I would highly recommend this service"
              </Typography>
            </Box>
        </Box>
    
    <Footer />    
  </PageContainer >
)
}

export default HomePage